import React from 'react';
import { Provider } from 'react-redux';
import { store } from '../store';
import { useSODPTickets } from '../hooks/useSODPTickets';

// Basic SODP Dashboard component (will be enhanced with UDS in Phase 3)
const SODPDashboardContent: React.FC = () => {
  const {
    tickets,
    loading,
    error,
    lastFetched,
    filterOptions,
    filters,
    refreshTickets,
    updateFilters,
    resetFilters,
    dismissError,
  } = useSODPTickets();

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading SODP tickets...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        <div>Error loading tickets: {error}</div>
        <button onClick={dismissError} style={{ marginTop: '10px', marginRight: '10px' }}>
          Dismiss
        </button>
        <button onClick={refreshTickets} style={{ marginTop: '10px' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>SODP Dashboard</h2>
        <div>
          <button onClick={refreshTickets} style={{ marginRight: '10px' }}>
            Refresh
          </button>
          <button onClick={resetFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {lastFetched && (
        <div style={{ marginBottom: '20px', fontSize: '14px', color: '#666' }}>
          Last updated: {new Date(lastFetched).toLocaleString()}
        </div>
      )}

      {/* Filter Controls */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px' }}>
        <h4>Filters</h4>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {/* Status Filter */}
          <div>
            <label>Status:</label>
            <select
              multiple
              value={filters.status}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                updateFilters({ status: values });
              }}
              style={{ marginLeft: '5px', minWidth: '120px' }}
            >
              {filterOptions.status.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label>Priority:</label>
            <select
              multiple
              value={filters.priority}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                updateFilters({ priority: values });
              }}
              style={{ marginLeft: '5px', minWidth: '120px' }}
            >
              {filterOptions.priority.map(priority => (
                <option key={priority} value={priority}>{priority}</option>
              ))}
            </select>
          </div>

          {/* Assignee Filter */}
          <div>
            <label>Assignee:</label>
            <select
              multiple
              value={filters.assignee}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                updateFilters({ assignee: values });
              }}
              style={{ marginLeft: '5px', minWidth: '120px' }}
            >
              {filterOptions.assignee.map(assignee => (
                <option key={assignee} value={assignee}>{assignee}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tickets Summary */}
      <div style={{ marginBottom: '20px' }}>
        <strong>Showing {tickets.length} tickets</strong>
        {(filters.status.length > 0 || filters.priority.length > 0 || filters.assignee.length > 0) && (
          <span style={{ color: '#666' }}> (filtered)</span>
        )}
      </div>

      {/* Tickets List */}
      <div style={{ display: 'grid', gap: '15px' }}>
        {tickets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No tickets found. Try adjusting your filters or check back later.
          </div>
        ) : (
          tickets.map(ticket => (
            <div
              key={ticket.id}
              style={{
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '15px',
                backgroundColor: '#fff',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 10px 0' }}>
                    <a
                      href={`https://telus-cio.atlassian.net/browse/${ticket.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: '#0066cc' }}
                    >
                      {ticket.key}
                    </a>
                    : {ticket.summary}
                  </h4>
                  {ticket.description && (
                    <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                      {ticket.description.substring(0, 200)}
                      {ticket.description.length > 200 && '...'}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '15px', fontSize: '14px' }}>
                    <span><strong>Status:</strong> {ticket.status}</span>
                    <span><strong>Priority:</strong> {ticket.priority}</span>
                    {ticket.assignee && <span><strong>Assignee:</strong> {ticket.assignee}</span>}
                  </div>
                  <div style={{ marginTop: '5px', fontSize: '12px', color: '#999' }}>
                    Created: {new Date(ticket.created).toLocaleDateString()} | 
                    Updated: {new Date(ticket.updated).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Main component wrapped with Redux Provider
const SODPDashboard: React.FC = () => {
  return (
    <Provider store={store}>
      <SODPDashboardContent />
    </Provider>
  );
};

export default SODPDashboard;
